import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, FlatList, Alert, ActivityIndicator, Platform, StatusBar, Image } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
// expo-contacts is only available on Android - excluded from iOS build
// to avoid App Store rejection for unused contacts permission
let Contacts: typeof import('expo-contacts') | null = null;
if (Platform.OS === 'android') {
  Contacts = require('expo-contacts');
}
import * as NavigationBar from 'expo-navigation-bar';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { RootStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import { storage } from '../../utils/storage';
import { buildApiUrl } from '../../config/env';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../theme/ThemeContext';

type Props = StackScreenProps<RootStackParamList, 'FindFriends'>;

const FIND_FRIENDS_COMPLETED_KEY = '@lnb_find_friends_completed';

interface MatchedContact {
  id: string;
  name: string | null;
  profession: string | null;
  city: string | null;
  photo: string | null;
  phoneNumber: string;
}

const FindFriendsScreen: React.FC<Props> = () => {
  const { reloadUser } = useAuth();
  const { theme, isDark } = useTheme();
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'pending'>('pending');
  const [loading, setLoading] = useState(false);
  const [matchedContacts, setMatchedContacts] = useState<MatchedContact[]>([]);
  const [sentRequests, setSentRequests] = useState<Set<string>>(new Set());
  const [sendingRequest, setSendingRequest] = useState<string | null>(null);

  useEffect(() => {
    if (Platform.OS === 'android') {
      NavigationBar.setButtonStyleAsync(isDark ? 'light' : 'dark').catch(() => {});
    }
  }, [isDark]);

  const requestContactsPermission = async () => {
    try {
      if (!Contacts) {
        setPermissionStatus('denied');
        return;
      }
      const { status } = await Contacts.requestPermissionsAsync();
      
      if (status === 'granted') {
        setPermissionStatus('granted');
        await loadContacts();
      } else {
        setPermissionStatus('denied');
      }
    } catch (error) {
      console.error('Error requesting contacts permission:', error);
      Alert.alert('Error', 'Failed to request contacts permission');
      setPermissionStatus('denied');
    }
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      // Get all contacts
      if (!Contacts) return;
      const { data } = await Contacts.getContactsAsync({
        fields: [Contacts.Fields.PhoneNumbers],
      });

      if (!data || data.length === 0) {
        setLoading(false);
        return;
      }

      // Extract phone numbers
      const phoneNumbers: string[] = [];
      data.forEach(contact => {
        if (contact.phoneNumbers) {
          contact.phoneNumbers.forEach(phoneNumber => {
            if (phoneNumber.number) {
              // Clean phone number (remove spaces, dashes, etc.)
              const cleaned = phoneNumber.number.replace(/[\s\-\(\)]/g, '');
              phoneNumbers.push(cleaned);
            }
          });
        }
      });

      if (phoneNumbers.length === 0) {
        setLoading(false);
        return;
      }

      // Send to backend to match with existing users
      await matchContactsWithUsers(phoneNumbers);
    } catch (error) {
      console.error('Error loading contacts:', error);
      Alert.alert('Error', 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const matchContactsWithUsers = async (phoneNumbers: string[]) => {
    try {
      const token = await storage.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiBaseUrl = await buildApiUrl();
      const response = await fetch(`${apiBaseUrl}/api/users/match-contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumbers }),
      });

      if (!response.ok) {
        throw new Error('Failed to match contacts');
      }

      const data = await response.json();
      setMatchedContacts(data.matches || []);
    } catch (error) {
      console.error('Error matching contacts:', error);
      // Don't show error to user, just show empty state
      setMatchedContacts([]);
    }
  };

  const handleSendRequest = async (userId: string) => {
    setSendingRequest(userId);
    try {
      const token = await storage.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const apiBaseUrl = await buildApiUrl();
      const response = await fetch(`${apiBaseUrl}/api/network/requests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          toUserId: userId,
          connectionType: 'others' // Default connection type
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send connection request');
      }

      // Add to sent requests
      setSentRequests(prev => new Set(prev).add(userId));
    } catch (error: any) {
      console.error('Error sending connection request:', error);
      Alert.alert('Error', error.message || 'Failed to send connection request');
    } finally {
      setSendingRequest(null);
    }
  };

  const handleSkip = async () => {
    // Mark FindFriends as completed
    await AsyncStorage.setItem(FIND_FRIENDS_COMPLETED_KEY, 'true');
    console.log('✅ FindFriends completed, navigating to main app...');
    
    // Reload user to trigger navigation to main app
    // RootNavigator will detect onboardingCompleted=true and show MainTabs
    await reloadUser();
  };

  const renderPermissionRequest = () => (
    <View style={styles.centerContainer}>
      <View style={styles.iconContainer}>
        <Ionicons name="people-outline" size={80} color={theme.accent} />
      </View>
      
      <ThemedText style={[styles.title, { color: theme.text }]}>Find Your Friends</ThemedText>
      <ThemedText style={[styles.description, { color: theme.textSecondary }]}>
        Connect with people you know who are already on the platform. We'll match your contacts with existing users.
      </ThemedText>

      <TouchableOpacity
        style={[styles.primaryButton, { backgroundColor: theme.accent }]}
        onPress={requestContactsPermission}
        activeOpacity={0.8}
      >
        <ThemedText style={[styles.buttonText, { color: '#000000' }]}>
          Allow Access to Contacts
        </ThemedText>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.skipButton}
        onPress={handleSkip}
        activeOpacity={0.7}
      >
        <ThemedText style={[styles.skipButtonText, { color: theme.textSecondary }]}>
          Skip for Now
        </ThemedText>
      </TouchableOpacity>
    </View>
  );

  const renderPermissionDenied = () => (
    <View style={styles.centerContainer}>
      <View style={styles.iconContainer}>
        <Ionicons name="close-circle-outline" size={80} color={theme.textSecondary} />
      </View>
      
      <ThemedText style={[styles.title, { color: theme.text }]}>Permission Denied</ThemedText>
      <ThemedText style={[styles.description, { color: theme.textSecondary }]}>
        You can enable contacts access later in your device settings to find friends on the platform.
      </ThemedText>

      <TouchableOpacity
        style={[styles.primaryButton, { backgroundColor: theme.accent }]}
        onPress={handleSkip}
        activeOpacity={0.8}
      >
        <ThemedText style={[styles.buttonText, { color: '#000000' }]}>
          Continue
        </ThemedText>
      </TouchableOpacity>
    </View>
  );

  const renderContactItem = ({ item }: { item: MatchedContact }) => {
    const isSent = sentRequests.has(item.id);
    const isSending = sendingRequest === item.id;

    return (
      <View style={[styles.contactCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <View style={styles.contactInfo}>
          {item.photo ? (
            <Image source={{ uri: item.photo }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, styles.avatarPlaceholder, { backgroundColor: theme.border }]}>
              <ThemedText style={[styles.avatarText, { color: theme.text }]}>
                {item.name?.charAt(0).toUpperCase() || '?'}
              </ThemedText>
            </View>
          )}
          
          <View style={styles.contactDetails}>
            <ThemedText style={[styles.contactName, { color: theme.text }]}>{item.name || 'Unknown'}</ThemedText>
            {item.profession && (
              <ThemedText style={[styles.contactProfession, { color: theme.textSecondary }]}>
                {item.profession}
              </ThemedText>
            )}
            {item.city && (
              <ThemedText style={[styles.contactCity, { color: theme.textSecondary }]}>
                {item.city}
              </ThemedText>
            )}
          </View>
        </View>

        {isSent ? (
          <View style={[styles.sentButton, { backgroundColor: theme.border }]}>
            <Ionicons name="checkmark-circle" size={18} color={theme.accent} />
            <ThemedText style={[styles.sentButtonText, { color: theme.accent }]}>
              Sent
            </ThemedText>
          </View>
        ) : (
          <TouchableOpacity
            style={[styles.connectButton, { backgroundColor: theme.accent }]}
            onPress={() => handleSendRequest(item.id)}
            disabled={isSending}
            activeOpacity={0.7}
          >
            {isSending ? (
              <ActivityIndicator size="small" color="#000000" />
            ) : (
              <>
                <Ionicons name="person-add" size={18} color="#000000" />
                <ThemedText style={[styles.connectButtonText, { color: '#000000' }]}>
                  Connect
                </ThemedText>
              </>
            )}
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderContactsList = () => (
    <View style={styles.container}>
      <View style={styles.header}>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
          {matchedContacts.length > 0 
            ? `${matchedContacts.length} Friend${matchedContacts.length === 1 ? '' : 's'} Found`
            : 'No Contacts Found'
          }
        </ThemedText>
        <ThemedText style={[styles.headerSubtitle, { color: theme.textSecondary }]}>
          {matchedContacts.length > 0 
            ? 'Send connection requests to people you know'
            : 'None of your contacts are on the platform yet'
          }
        </ThemedText>
      </View>

      {matchedContacts.length > 0 ? (
        <FlatList
          data={matchedContacts}
          renderItem={renderContactItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
        />
      ) : (
        <View style={styles.emptyContainer}>
          <Ionicons name="people-outline" size={60} color={theme.textSecondary} />
          <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
            No contacts found on the platform
          </ThemedText>
        </View>
      )}

      <View style={[styles.footer, { backgroundColor: theme.background }]}>
        <TouchableOpacity
          style={[styles.continueButton, { backgroundColor: theme.accent }]}
          onPress={handleSkip}
          activeOpacity={0.8}
        >
          <ThemedText style={[styles.buttonText, { color: '#000000' }]}>
            Continue
          </ThemedText>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      <StatusBar barStyle={isDark ? 'light-content' : 'dark-content'} backgroundColor={theme.background} translucent={false} />
      <SafeAreaView style={styles.safeArea} edges={['top', 'bottom']}>
        {loading ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={theme.accent} />
            <ThemedText style={[styles.loadingText, { color: theme.textSecondary }]}>
              Finding your friends...
            </ThemedText>
          </View>
        ) : permissionStatus === 'pending' ? (
          renderPermissionRequest()
        ) : permissionStatus === 'denied' ? (
          renderPermissionDenied()
        ) : (
          renderContactsList()
        )}
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  safeArea: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  iconContainer: {
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 12,
  },
  description: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    opacity: 0.8,
  },
  primaryButton: {
    width: '100%',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  skipButton: {
    paddingVertical: 12,
  },
  skipButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  header: {
    padding: 24,
    paddingBottom: 16,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    lineHeight: 20,
  },
  listContainer: {
    paddingHorizontal: 24,
    paddingBottom: 100,
  },
  contactCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
  },
  contactInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginRight: 12,
  },
  avatarPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 20,
    fontWeight: '600',
  },
  contactDetails: {
    flex: 1,
  },
  contactName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  contactProfession: {
    fontSize: 14,
    marginBottom: 2,
  },
  contactCity: {
    fontSize: 12,
  },
  connectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  connectButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  sentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  sentButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 24,
  },
  continueButton: {
    width: '100%',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default FindFriendsScreen;
